import { createAppRequest } from '@/services/request';
import type {
  AdvancementDetail,
  AdvancementDocItem,
  AdvancementTaskItem,
  ContactRecordAggregate,
  ContactRecordItem,
  CustomerDetailAggregate,
  CustomerDetailCustomerProfile,
  FollowupScriptItem,
  IndustryTag,
  KeyPerson,
  KnownIssue,
  KnownIssueMutationInput,
  LeadershipFocusItem,
  MaterialItem,
  MaterialSummary,
  OpportunityItem,
  OpportunityAdvancementItem,
  OrgGraphNode,
  SystemItem,
  TagTaxonomy,
  TransformationStageInfo,
} from './types/customer-intelligence';

const request = createAppRequest();
const BASE_PATH = '/bff/customer-intelligence/customers';

export const getCustomerDetail = (customerUuid: string) =>
  request<CustomerDetailAggregate>(`${BASE_PATH}/${customerUuid}/detail`, {
    method: 'GET',
  });

export const updateCustomerProfile = (
  customerUuid: string,
  data: Partial<CustomerDetailCustomerProfile>,
) =>
  request<CustomerDetailCustomerProfile>(`${BASE_PATH}/${customerUuid}/profile`, {
    method: 'PUT',
    data,
  });

export const listOrgGraph = (customerUuid: string) =>
  request<OrgGraphNode[]>(`${BASE_PATH}/${customerUuid}/org-graph`, {
    method: 'GET',
  });

export const createOrgNode = (customerUuid: string, data: Partial<OrgGraphNode>) =>
  request<OrgGraphNode>(`${BASE_PATH}/${customerUuid}/org-graph`, {
    method: 'POST',
    data,
  });

export const updateOrgNode = (
  customerUuid: string,
  nodeUuid: string,
  data: Partial<OrgGraphNode>,
) =>
  request<OrgGraphNode>(`${BASE_PATH}/${customerUuid}/org-graph/${nodeUuid}`, {
    method: 'PUT',
    data,
  });

export const deleteOrgNode = (customerUuid: string, nodeUuid: string) =>
  request<{ success: boolean }>(`${BASE_PATH}/${customerUuid}/org-graph/${nodeUuid}`, {
    method: 'DELETE',
  });

export const reorderOrgNode = (
  customerUuid: string,
  data: { nodeUuid: string; parentNodeUuid?: string | null; sortOrder?: number },
) =>
  request<OrgGraphNode[]>(`${BASE_PATH}/${customerUuid}/org-graph/reorder`, {
    method: 'PATCH',
    data,
  });

export const listKeyPeople = (customerUuid: string) =>
  request<KeyPerson[]>(`${BASE_PATH}/${customerUuid}/key-people`, {
    method: 'GET',
  });

export const createKeyPerson = (customerUuid: string, data: Partial<KeyPerson>) =>
  request<KeyPerson>(`${BASE_PATH}/${customerUuid}/key-people`, {
    method: 'POST',
    data,
  });

export const updateKeyPerson = (
  customerUuid: string,
  personUuid: string,
  data: Partial<KeyPerson>,
) =>
  request<KeyPerson>(`${BASE_PATH}/${customerUuid}/key-people/${personUuid}`, {
    method: 'PUT',
    data,
  });

export const deleteKeyPerson = (customerUuid: string, personUuid: string) =>
  request<{ success: boolean }>(`${BASE_PATH}/${customerUuid}/key-people/${personUuid}`, {
    method: 'DELETE',
  });

export const listCustomerTags = (customerUuid: string) =>
  request<TagTaxonomy[]>(`${BASE_PATH}/${customerUuid}/tags`, {
    method: 'GET',
  });

export const createCustomTag = (
  customerUuid: string,
  data: { taxonomyUuid: string; name: string },
) =>
  request<IndustryTag>(`${BASE_PATH}/${customerUuid}/tags/custom`, {
    method: 'POST',
    data,
  });

export const updateCustomerTagSelection = (
  customerUuid: string,
  taxonomyUuid: string,
  tagUuids: string[],
) =>
  request<TagTaxonomy[]>(`${BASE_PATH}/${customerUuid}/tags/${taxonomyUuid}/selection`, {
    method: 'PUT',
    data: { tagUuids },
  });

export const listSystems = (customerUuid: string) =>
  request<SystemItem[]>(`${BASE_PATH}/${customerUuid}/systems`, {
    method: 'GET',
  });

export const createSystem = (customerUuid: string, data: Partial<SystemItem>) =>
  request<SystemItem>(`${BASE_PATH}/${customerUuid}/systems`, {
    method: 'POST',
    data,
  });

export const updateSystem = (
  customerUuid: string,
  systemUuid: string,
  data: Partial<SystemItem>,
) =>
  request<SystemItem>(`${BASE_PATH}/${customerUuid}/systems/${systemUuid}`, {
    method: 'PUT',
    data,
  });

export const deleteSystem = (customerUuid: string, systemUuid: string) =>
  request<{ success: boolean }>(`${BASE_PATH}/${customerUuid}/systems/${systemUuid}`, {
    method: 'DELETE',
  });

export const getTransformationStage = (customerUuid: string) =>
  request<TransformationStageInfo>(`${BASE_PATH}/${customerUuid}/transformation-stage`, {
    method: 'GET',
  });

export const updateTransformationStage = (
  customerUuid: string,
  data: Partial<TransformationStageInfo>,
) =>
  request<TransformationStageInfo>(`${BASE_PATH}/${customerUuid}/transformation-stage`, {
    method: 'PUT',
    data,
  });

export const listMaterials = (customerUuid: string) =>
  request(`${BASE_PATH}/${customerUuid}/materials`, {
    method: 'GET',
  });

export const createMaterial = (customerUuid: string, data: Partial<MaterialItem>) =>
  request<MaterialItem>(`${BASE_PATH}/${customerUuid}/materials`, {
    method: 'POST',
    data,
  });

export const updateMaterial = (
  customerUuid: string,
  materialUuid: string,
  data: Partial<MaterialItem>,
) =>
  request<MaterialItem>(`${BASE_PATH}/${customerUuid}/materials/${materialUuid}`, {
    method: 'PUT',
    data,
  });

export const deleteMaterial = (customerUuid: string, materialUuid: string) =>
  request<{ success: boolean }>(`${BASE_PATH}/${customerUuid}/materials/${materialUuid}`, {
    method: 'DELETE',
  });

export const triggerMaterialSummary = (customerUuid: string, materialUuid: string) =>
  request<MaterialSummary>(
    `${BASE_PATH}/${customerUuid}/materials/${materialUuid}/summarize`,
    {
      method: 'POST',
    },
  );

export const listLeadershipFocuses = (customerUuid: string) =>
  request<LeadershipFocusItem[]>(`${BASE_PATH}/${customerUuid}/leadership-focuses`, {
    method: 'GET',
  });

export const createLeadershipFocus = (
  customerUuid: string,
  data: Partial<LeadershipFocusItem>,
) =>
  request<LeadershipFocusItem>(`${BASE_PATH}/${customerUuid}/leadership-focuses`, {
    method: 'POST',
    data,
  });

export const updateLeadershipFocus = (
  customerUuid: string,
  focusUuid: string,
  data: Partial<LeadershipFocusItem>,
) =>
  request<LeadershipFocusItem>(
    `${BASE_PATH}/${customerUuid}/leadership-focuses/${focusUuid}`,
    {
      method: 'PUT',
      data,
    },
  );

export const deleteLeadershipFocus = (customerUuid: string, focusUuid: string) =>
  request<{ success: boolean }>(
    `${BASE_PATH}/${customerUuid}/leadership-focuses/${focusUuid}`,
    {
      method: 'DELETE',
    },
  );

export const listContacts = (customerUuid: string) =>
  request<ContactRecordAggregate>(`${BASE_PATH}/${customerUuid}/contacts`, {
    method: 'GET',
  });

export const createContact = (customerUuid: string, data: Partial<ContactRecordItem>) =>
  request<ContactRecordItem>(`${BASE_PATH}/${customerUuid}/contacts`, {
    method: 'POST',
    data,
  });

export const importContacts = (customerUuid: string, items: Partial<ContactRecordItem>[]) =>
  request<{ count: number; items: ContactRecordItem[] }>(
    `${BASE_PATH}/${customerUuid}/contacts/import`,
    {
      method: 'POST',
      data: { items },
    },
  );

export const updateContact = (
  customerUuid: string,
  contactUuid: string,
  data: Partial<ContactRecordItem>,
) =>
  request<ContactRecordItem>(`${BASE_PATH}/${customerUuid}/contacts/${contactUuid}`, {
    method: 'PUT',
    data,
  });

export const deleteContact = (customerUuid: string, contactUuid: string) =>
  request<{ success: boolean }>(`${BASE_PATH}/${customerUuid}/contacts/${contactUuid}`, {
    method: 'DELETE',
  });

export const listKnownIssues = (customerUuid: string) =>
  request<KnownIssue[]>(`${BASE_PATH}/${customerUuid}/known-issues`, {
    method: 'GET',
  });

export const createKnownIssue = (
  customerUuid: string,
  data: KnownIssueMutationInput,
) =>
  request<KnownIssue>(`${BASE_PATH}/${customerUuid}/known-issues`, {
    method: 'POST',
    data,
  });

export const updateKnownIssue = (
  customerUuid: string,
  issueUuid: string,
  data: KnownIssueMutationInput,
) =>
  request<KnownIssue>(`${BASE_PATH}/${customerUuid}/known-issues/${issueUuid}`, {
    method: 'PUT',
    data,
  });

export const deleteKnownIssue = (customerUuid: string, issueUuid: string) =>
  request<{ success: boolean }>(`${BASE_PATH}/${customerUuid}/known-issues/${issueUuid}`, {
    method: 'DELETE',
  });

export const listOpportunities = (customerUuid: string) =>
  request<OpportunityItem[]>(`${BASE_PATH}/${customerUuid}/opportunities`, {
    method: 'GET',
  });

export const createOpportunity = (
  customerUuid: string,
  data: Partial<OpportunityItem>,
) =>
  request<OpportunityItem>(`${BASE_PATH}/${customerUuid}/opportunities`, {
    method: 'POST',
    data,
  });

export const updateOpportunity = (
  customerUuid: string,
  opportunityUuid: string,
  data: Partial<OpportunityItem>,
) =>
  request<OpportunityItem>(`${BASE_PATH}/${customerUuid}/opportunities/${opportunityUuid}`, {
    method: 'PUT',
    data,
  });

export const deleteOpportunity = (customerUuid: string, opportunityUuid: string) =>
  request<{ success: boolean }>(
    `${BASE_PATH}/${customerUuid}/opportunities/${opportunityUuid}`,
    {
      method: 'DELETE',
    },
  );

export const getAdvancementDetail = (customerUuid: string, opportunityUuid: string) =>
  request<AdvancementDetail>(`${BASE_PATH}/${customerUuid}/opportunities/${opportunityUuid}/advancement/detail`, {
    method: 'GET',
  });

export const upsertAdvancement = (
  customerUuid: string,
  opportunityUuid: string,
  data: Record<string, unknown>,
) =>
  request(`${BASE_PATH}/${customerUuid}/opportunities/${opportunityUuid}/advancement`, {
    method: 'PUT',
    data,
  });

export const listAdvancementTasks = (customerUuid: string, opportunityUuid: string) =>
  request<AdvancementTaskItem[]>(
    `${BASE_PATH}/${customerUuid}/opportunities/${opportunityUuid}/advancement/tasks`,
    {
      method: 'GET',
    },
  );

export const createAdvancementTask = (
  customerUuid: string,
  opportunityUuid: string,
  data: Partial<AdvancementTaskItem>,
) =>
  request<AdvancementTaskItem>(
    `${BASE_PATH}/${customerUuid}/opportunities/${opportunityUuid}/advancement/tasks`,
    {
      method: 'POST',
      data,
    },
  );

export const updateAdvancementTask = (
  customerUuid: string,
  opportunityUuid: string,
  taskUuid: string,
  data: Partial<AdvancementTaskItem>,
) =>
  request<AdvancementTaskItem>(
    `${BASE_PATH}/${customerUuid}/opportunities/${opportunityUuid}/advancement/tasks/${taskUuid}`,
    {
      method: 'PUT',
      data,
    },
  );

export const deleteAdvancementTask = (
  customerUuid: string,
  opportunityUuid: string,
  taskUuid: string,
) =>
  request<{ success: boolean }>(
    `${BASE_PATH}/${customerUuid}/opportunities/${opportunityUuid}/advancement/tasks/${taskUuid}`,
    {
      method: 'DELETE',
    },
  );

export const listOverdueAdvancementTasks = () =>
  request<{ total: number; items: Array<AdvancementTaskItem & { opportunity?: OpportunityItem | null }> }>(
    '/bff/customer-intelligence/advancement/tasks/overdue',
    { method: 'GET' },
  );

export const listAdvancementBoard = (params?: {
  stage?: string;
  overdueOnly?: boolean;
  keyword?: string;
}) =>
  request<{
    total: number;
    items: Array<{
      opportunity: OpportunityItem;
      customer?: { customerName?: string | null } | null;
      advancement?: OpportunityAdvancementItem | null;
      overdueCount: number;
      nextDueDate?: string | null;
      latestDocAt?: string | null;
    }>;
  }>('/bff/customer-intelligence/advancement/board', {
    method: 'GET',
    params,
  });

export const generateAdvancementDoc = (
  customerUuid: string,
  opportunityUuid: string,
  data: {
    docType: AdvancementDocItem['docType'];
    audienceStyle: AdvancementDocItem['audienceStyle'];
  },
) =>
  request<AdvancementDocItem>(
    `${BASE_PATH}/${customerUuid}/opportunities/${opportunityUuid}/advancement/docs/generate`,
    {
      method: 'POST',
      data,
    },
  );

export const listAdvancementDocs = (
  customerUuid: string,
  opportunityUuid: string,
  params?: Partial<Pick<AdvancementDocItem, 'docType' | 'audienceStyle'>>,
) =>
  request<AdvancementDocItem[]>(
    `${BASE_PATH}/${customerUuid}/opportunities/${opportunityUuid}/advancement/docs`,
    {
      method: 'GET',
      params,
    },
  );

export const generateFollowupScript = (
  customerUuid: string,
  opportunityUuid: string,
  data: { scene: FollowupScriptItem['scene']; tone?: string },
) =>
  request<FollowupScriptItem>(
    `${BASE_PATH}/${customerUuid}/opportunities/${opportunityUuid}/advancement/scripts/generate`,
    {
      method: 'POST',
      data,
    },
  );
